import { runInAction } from 'mobx';
import type { BaseStore } from './stores/base';

// Create a new worker instance with Vite's worker syntax
const createHydrationWorker = () => {
  return new Worker(
    new URL('../workers/hydration.worker.ts', import.meta.url),
    { type: 'module' },
  );
};

type WorkerHydrationConfig<Model, T> = {
  tableName: string;
  entity: string;
  modelStore: BaseStore<Model, T>;
  ids?: string[];
};

export async function hydrateFromWorker<Model, T>(
  config: WorkerHydrationConfig<Model, T>,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const worker = createHydrationWorker();

    // Set up message handler
    worker.onmessage = (event) => {
      const { data, error } = event.data;

      if (error) {
        worker.terminate();
        reject(new Error(error));
        return;
      }

      // Process the data in batches to avoid blocking the main thread
      const batchSize = 100;
      let currentIndex = 0;

      const processBatch = () => {
        const endIndex = Math.min(currentIndex + batchSize, data.length);
        const batch = data.slice(currentIndex, endIndex);

        // Upsert each record into the model store
        runInAction(() => {
          batch.forEach((attrs: T) => {
            if (config.ids && !config.ids.includes((attrs as any).id)) return;

            config.modelStore.upsert(attrs);
          });
        });

        currentIndex = endIndex;

        // Continue processing if there are more records
        if (currentIndex < data.length) {
          processBatch();
        } else {
          // All records processed, cleanup and resolve
          worker.terminate();
          resolve();
        }
      };

      // Start processing
      processBatch();
    };

    // Handle errors
    worker.onerror = (error) => {
      worker.terminate();
      reject(error);
    };

    // Send the hydration request to the worker
    worker.postMessage({
      type: 'hydrateFromDB',
      tableName: config.tableName,
      entity: config.entity,
    });
  });
}
